import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  ChevronDown,
  Monitor,
  Moon,
  SlidersHorizontal,
  Sun,
  X,
} from "lucide-preact";

import { detectRegime } from "../consent/geo";
import {
  clearConsent,
  type ConsentRecord,
  loadConsent,
  saveConsent,
} from "../consent/state";
import { forgetMe } from "../identity/bootstrap";
import { getDeviceId } from "../identity/device";
import {
  type InterestVector,
  loadInterestVector,
  saveInterestVector,
} from "../feed/interest";
import { useBlockedPublishers } from "../feed/blockedPublishers";
import { rankArticles, type RankParams } from "../feed/rank";
import type { ManifestEntry } from "../feed/types";
import { TOPIC_QUESTIONS } from "../onboarding/topics";
import { type ThemeChoice, useTheme } from "../shell/theme";
import { pushBodyScrollLock } from "../util/bodyScrollLock";

/** Theme segmented-control options, in display order. Icon + label per
 *  choice; the value maps 1:1 to ThemeChoice. */
const THEME_OPTIONS: ReadonlyArray<{
  value: ThemeChoice;
  label: string;
  Icon: typeof Sun;
}> = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

/** A tag is "on" in the topic picker when its weight is above this
 *  threshold. Picked so a tag the user explicitly selected (default
 *  weight 1.0) shows as on, and a tag that got a small engagement
 *  nudge (e.g. dwell_30s at delta=0.3) doesn't masquerade as a
 *  deliberate pick. */
const TOPIC_ON_THRESHOLD = 0.5;

/** Show the blocked-publishers filter input only once the list is at
 *  least this long — below it, scanning is faster than typing. */
const BLOCKED_FILTER_THRESHOLD = 6;

/** How many top stories the live re-rank preview shows. Small enough to
 *  stay glanceable at the top of the drawer while you tune. */
const PREVIEW_COUNT = 5;

import { Radar } from "./Radar";
import { PRESETS, type PresetName, RANK_PREFS_BOUNDS } from "./rankPrefs";

/** Animation timing — mirrors the CSS `transition: transform 240ms` on
 *  `.menu-drawer`. The unmount delay slightly exceeds the CSS so the
 *  panel doesn't vanish mid-animation. Same shape as ArticleDetail.tsx
 *  intentionally — one mental model for both slide-outs. */
const SLIDE_DURATION_MS = 240;
const UNMOUNT_DELAY_MS = SLIDE_DURATION_MS + 20;

/** Unique history-marker generator. Distinct per mount so a stray
 *  `menuDrawer: true` pushed by some future surface can't make our
 *  popstate cleanup pop someone else's entry. Mirrors ArticleDetail's
 *  `freshMarker()` pattern. */
let nextMarkerId = 0;
function freshMarker(): number {
  nextMarkerId += 1;
  return nextMarkerId;
}

export interface SettingsProps {
  onClose: () => void;
  /** Called after a successful "Forget me" so the shell can reset to the
   * pre-consent state (consent banner reappears, personalization off). */
  onForgotten: () => void;
  /** Current ranking params. The drawer mutates these through the setter
   *  on every slider/preset change; the Feed re-renders with the new
   *  weights on the same frame, so the user sees instant re-rank. */
  rankParams: RankParams;
  setRankParams: (next: RankParams) => void;
  /** When personalization is off (declined / pre-consent), the device-id
   *  + Forget-me block is hidden — there's no device id to show. The
   *  ranking knobs still work because rank.ts treats a null interest
   *  vector as a no-op. The drawer always exposes the on/off toggle so
   *  a declining user has a path back without nuking storage. */
  personalized: boolean;
  /** Called with the new consent record when the user flips the
   *  personalization switch in the drawer. Accepting flips `personalized`
   *  true and kicks off identity bootstrap + onboarding (same chain the
   *  consent banner uses); declining pauses re-ranking (feed reverts to
   *  base order) without nuking the on-device interest vector — that's
   *  what "Forget me" is for. */
  onConsentChange: (record: ConsentRecord) => void;
  /** The currently-loaded feed entries, for the live re-rank preview. The
   *  drawer covers the feed (full-screen on mobile), so the preview is how
   *  the reader watches their tuning take effect in real time. */
  previewEntries?: readonly ManifestEntry[];
  /** Live interest vector from the shell — the preview re-ranks against it
   *  exactly as the feed does (null/empty → base order, mirroring the
   *  personalization gate). Distinct from the drawer's own `interest`
   *  snapshot, which drives the topic-picker chips. */
  liveInterest?: InterestVector | null;
}

export function Settings({
  onClose,
  onForgotten,
  rankParams,
  setRankParams,
  personalized,
  onConsentChange,
  previewEntries,
  liveInterest,
}: SettingsProps) {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Substring filter for the blocked-publishers list, shown once the list
  // is long enough to be worth searching.
  const [blockedFilter, setBlockedFilter] = useState("");
  // The fine-grained sliders start collapsed: most readers want a
  // one-tap preset, and the slider stack is long. The "Customize"
  // button reveals them for the readers who want knob-level control.
  const [showSliders, setShowSliders] = useState(false);
  // Spinner state for the "Turn on personalization" path. detectRegime
  // is a quick fetch to the edge worker's /api/geo; we surface the
  // wait so the user knows the tap registered.
  const [acceptingConsent, setAcceptingConsent] = useState(false);
  // Interest vector snapshot for the topic-picker chips. Loaded once
  // when the drawer opens (and again whenever the user just turned
  // personalization on inside the drawer); per-toggle updates write
  // back via saveInterestVector and re-set this snapshot so the chip
  // state stays in sync.
  const [interest, setInterest] = useState<InterestVector>({});
  // Blocked-publishers state shared with Feed.tsx — same `agg:blocked_
  // publishers:v1` localStorage key, so writes here (unblock from the
  // chip list) propagate to the feed filter on the next render.
  const { blocked: blockedPublishers, unblock: unblockPublisher } =
    useBlockedPublishers();
  const [themeChoice, setThemeChoice] = useTheme();
  // Drives the slide-in animation. Mounted at `translateX(-100%)`, flipped
  // to `translateX(0)` on the next animation frame so the transform
  // transitions in. Mirrors ArticleDetail.tsx but anchored to the left
  // edge (left = canonical "menu" pattern; right = content cover).
  const [open, setOpen] = useState(false);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // Unique marker for this instance's `history.state.menuDrawer`.
  // Equality-compared on cleanup so we never pop a marker we didn't push.
  const historyMarker = useRef<number>(freshMarker());

  useEffect(() => {
    let cancelled = false;
    void getDeviceId().then((id) => {
      if (!cancelled) setDeviceId(id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the persisted interest vector once so the topic chips can
  // render their on/off state. Re-runs when `personalized` flips true
  // (the user just turned it on inside the drawer) so chips appear
  // immediately rather than after a reload. The vector is local-only
  // (IndexedDB); the load is cheap.
  useEffect(() => {
    if (!personalized) {
      setInterest({});
      return;
    }
    let cancelled = false;
    void loadInterestVector().then((vec) => {
      if (!cancelled) setInterest(vec ?? {});
    });
    return () => {
      cancelled = true;
    };
  }, [personalized]);

  // Slide-in on mount; symmetric slide-out + deferred unmount on close.
  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const closeWithAnim = () => {
    setOpen(false);
    if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
    unmountTimer.current = setTimeout(() => {
      unmountTimer.current = null;
      onClose();
    }, UNMOUNT_DELAY_MS);
  };

  // Body scroll-lock while the drawer is open. Uses the shared refcount
  // module so a simultaneous ArticleDetail open doesn't leak the lock
  // (each surface used to capture `body.overflow` independently, and
  // restore order leaked the "hidden" value).
  useEffect(() => pushBodyScrollLock(), []);

  // ESC closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeWithAnim();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Android back gesture / iOS edge-swipe closes the drawer instead of
  // navigating away. Mirrors ArticleDetail.tsx for parity — same surface
  // category (mobile-first slide-out, aria-modal="true"), same user
  // expectation. The marker is unique per mount; cleanup pops it only
  // if it's still on top of the history stack (equality check, not
  // truthy check — defends against cross-surface collision).
  useEffect(() => {
    const marker = historyMarker.current;
    history.pushState({ menuDrawer: marker }, "");
    const onPop = () => {
      // Marker already popped — animate out, then call onClose without
      // re-popping. Reuse the timer ref so a parallel close path can
      // cancel this animation too.
      setOpen(false);
      if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
      unmountTimer.current = setTimeout(() => {
        unmountTimer.current = null;
        onClose();
      }, UNMOUNT_DELAY_MS);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // If unmount was triggered by something OTHER than the back
      // gesture (X button, backdrop tap, ESC), OUR marker is still on
      // the stack. Pop it so the user's next "back" doesn't no-op.
      if (history.state?.menuDrawer === marker) history.back();
    };
  }, []);

  // Focus management: focus the close button after the slide-in commits
  // (double rAF — first frame applies `.open`, second frame focuses so
  // the element is on-screen). Restore previous focus on unmount only if
  // the originating element is still attached.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    let inner: number | null = null;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        inner = null;
        closeButtonRef.current?.focus();
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner !== null) cancelAnimationFrame(inner);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      }
      if (unmountTimer.current !== null) {
        clearTimeout(unmountTimer.current);
        unmountTimer.current = null;
      }
    };
  }, []);

  /** Flip a single topic chip. "On" → set that tag's weight to 1.0;
   *  "off" → remove the tag from the vector entirely. Engagement-tuned
   *  weights on OTHER tags are untouched, so this is non-destructive
   *  to the rest of the user's learned interests. */
  const toggleTopic = async (tag: string) => {
    const currentlyOn = (interest[tag] ?? 0) >= TOPIC_ON_THRESHOLD;
    const next: InterestVector = { ...interest };
    if (currentlyOn) {
      delete next[tag];
    } else {
      next[tag] = 1.0;
    }
    setInterest(next);
    // saveInterestVector returns the actually-persisted (pruned) shape,
    // which can drop low-magnitude entries if we got near the 2 KB cap.
    // Mirror that back into local state so the chips reflect what was
    // really stored.
    const saved = await saveInterestVector(next);
    setInterest(saved);
  };

  const acceptPersonalization = async () => {
    if (acceptingConsent) return;
    setAcceptingConsent(true);
    try {
      // Same path the consent banner uses — detect regime, persist the
      // record, hand it back to the shell so the personalized effect
      // chain (bootstrapIdentity + interest vector load + onboarding
      // check) kicks off without forcing a page reload.
      const regime = await detectRegime();
      const record: ConsentRecord = {
        regime,
        decision: "accepted",
        decided_at: Date.now(),
      };
      saveConsent(record);
      onConsentChange(record);
    } finally {
      setAcceptingConsent(false);
    }
  };

  // Pause personalization: record a "declined" decision so the shell
  // flips `personalized` false and the feed reverts to base order. This
  // is deliberately NON-destructive — the on-device interest vector stays
  // in IndexedDB so flipping the switch back on restores the learned
  // profile. "Forget me" is the destructive path that erases it.
  const pausePersonalization = () => {
    const existing = loadConsent();
    const record: ConsentRecord = {
      regime: existing?.regime ?? "default",
      decision: "declined",
      decided_at: Date.now(),
    };
    saveConsent(record);
    onConsentChange(record);
  };

  const togglePersonalization = () => {
    if (acceptingConsent) return;
    if (personalized) pausePersonalization();
    else void acceptPersonalization();
  };

  const forget = async () => {
    setBusy(true);
    await forgetMe();
    clearConsent();
    setBusy(false);
    onForgotten();
  };

  // One-tap presets. Each writes a complete RankParams to state; the
  // Feed re-renders + re-ranks on the next render. User sees the cards
  // re-order immediately. `PresetName` is the closed union of the four
  // declared preset keys — a typo here is a compile error.
  const applyPreset = (name: PresetName) => {
    setRankParams({ ...PRESETS[name] });
  };

  /** Generic slider handler — one closure per RankParams field. The
   *  closure captures `key` so the `onInput` lambda in JSX stays tiny
   *  and we don't duplicate the parse+spread boilerplate per slider. */
  const updateField = (key: keyof RankParams) => (e: Event) => {
    const v = (e.currentTarget as HTMLInputElement).valueAsNumber;
    setRankParams({ ...rankParams, [key]: v });
  };
  const updateLambda = updateField("lambda");
  const updateRecencyWeight = updateField("recencyWeight");
  const updateHalfLife = (e: Event) => {
    const v = (e.currentTarget as HTMLInputElement).valueAsNumber;
    setRankParams({ ...rankParams, recencyHalfLifeHours: v });
  };

  // Top stories as the current settings would rank them. Recomputed on
  // every slider / preset / toggle change so the reader watches the order
  // shuffle live (the drawer covers the feed, so this is the window onto
  // the effect). Mirrors the feed exactly: drop blocked publishers, then
  // rank (a truthy `liveInterest` — including an empty `{}` — re-ranks;
  // only `null` keeps the server base order, same as Feed.tsx).
  const preview = useMemo<ManifestEntry[]>(() => {
    const base = (previewEntries ?? []).filter(
      (e) => !(e.publisher && blockedPublishers.has(e.publisher)),
    );
    const ranked = liveInterest
      ? rankArticles(base, liveInterest, rankParams)
      : base;
    return ranked.slice(0, PREVIEW_COUNT);
  }, [previewEntries, liveInterest, rankParams, blockedPublishers]);

  return (
    <div
      class={open ? "menu-backdrop open" : "menu-backdrop"}
      onClick={closeWithAnim}
    >
      <aside
        class={open ? "menu-drawer open" : "menu-drawer"}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="menu-header">
          <h2 id="settings-title">Settings</h2>
          <button
            class="menu-close"
            aria-label="Close"
            onClick={closeWithAnim}
            ref={closeButtonRef}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </header>

        <div class="menu-body">
          {/* Radar hero — the at-a-glance shape of the reader's
            * personalization profile. Lives at the top because it's the
            * striking visual; the sliders that drive it are just below
            * and the radar updates live as they move. */}
          <div class="radar-hero">
            <Radar params={rankParams} />
          </div>

          {/* Live re-rank preview — the reader's actual top stories, in the
            * order the current settings would rank them. Reshuffles in real
            * time as sliders / presets / the personalization switch change,
            * so the effect of tuning is visible even though the drawer
            * covers the feed itself. */}
          {preview.length > 0 ? (
            <div class="rank-preview">
              <div class="rank-preview-head">
                <span>Live preview</span>
                <span class="muted">
                  {personalized
                    ? "your top stories, ranked now"
                    : "default order — turn on personalization"}
                </span>
              </div>
              <ol class="rank-preview-list">
                {preview.map((e) => (
                  <li key={e.id} class="rank-preview-item">
                    {e.title}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <section class="settings-section">
            <h3>Appearance</h3>
            <div class="theme-toggle" role="group" aria-label="Theme">
              {THEME_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  class={
                    themeChoice === value
                      ? "theme-option active"
                      : "theme-option"
                  }
                  aria-pressed={themeChoice === value ? "true" : "false"}
                  onClick={() => setThemeChoice(value)}
                >
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section class="settings-section">
            <h3>Tune the feed</h3>
            <p class="muted">Pick a vibe — the feed re-orders instantly.</p>
            {!personalized ? (
              <p class="muted note-inactive">
                These take effect once <strong>Personalization</strong> is on
                (below).
              </p>
            ) : null}

            {/* Algorithm presets sit right under the radar so the shape
              * above re-morphs the instant you tap one. */}
            <div class="preset-row">
              <button
                class="preset"
                onClick={() => applyPreset("fresh")}
                aria-label="Preset: more fresh stories"
              >
                More fresh
              </button>
              <button
                class="preset"
                onClick={() => applyPreset("popular")}
                aria-label="Preset: more popular stories"
              >
                More popular
              </button>
              <button
                class="preset"
                onClick={() => applyPreset("matched")}
                aria-label="Preset: more matched to my interests"
              >
                More matched
              </button>
              <button
                class="preset"
                onClick={() => applyPreset("explorer")}
                aria-label="Preset: explorer — balanced diversity"
              >
                Explorer
              </button>
              <button
                class="preset preset-reset"
                onClick={() => applyPreset("default")}
                aria-label="Reset ranking to defaults"
              >
                Reset
              </button>
            </div>

            {/* Reveal the per-axis sliders for readers who want knob-level
              * control. Collapsed by default — the presets above cover the
              * common case and the slider stack is long. */}
            <button
              type="button"
              class="customize-toggle"
              aria-expanded={showSliders}
              aria-controls="tune-sliders"
              onClick={() => setShowSliders((v) => !v)}
            >
              <SlidersHorizontal size={16} aria-hidden="true" />
              <span>
                {showSliders ? "Hide sliders" : "Customize with sliders"}
              </span>
              <ChevronDown
                size={16}
                aria-hidden="true"
                class={
                  showSliders ? "customize-chevron open" : "customize-chevron"
                }
              />
            </button>

            {/* Always rendered (not conditionally) so the `aria-controls`
              * target always resolves; `hidden` does the visual collapse
              * when the reader hasn't opened it. */}
            <div class="slider-panel" id="tune-sliders" hidden={!showSliders}>
              <p class="muted">
                Slide and tap. The feed re-orders as you change a knob.
              </p>

                <label class="slider-row">
                  <span class="slider-label">
                    Match to your interests
                    <span class="slider-value">
                      {rankParams.lambda.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.lambda.min}
                    max={RANK_PREFS_BOUNDS.lambda.max}
                    step={RANK_PREFS_BOUNDS.lambda.step}
                    value={rankParams.lambda}
                    onInput={updateLambda}
                    aria-label="Match to your interests weight"
                    aria-valuetext={`${rankParams.lambda.toFixed(2)} of ${RANK_PREFS_BOUNDS.lambda.max}`}
                  />
                  <span class="slider-hint">
                    Higher = lean harder into your tags
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Prefer fresh stories
                    <span class="slider-value">
                      {rankParams.recencyWeight.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.recencyWeight.min}
                    max={RANK_PREFS_BOUNDS.recencyWeight.max}
                    step={RANK_PREFS_BOUNDS.recencyWeight.step}
                    value={rankParams.recencyWeight}
                    onInput={updateRecencyWeight}
                    aria-label="Recency weight"
                    aria-valuetext={`${rankParams.recencyWeight.toFixed(2)} of ${RANK_PREFS_BOUNDS.recencyWeight.max}`}
                  />
                  <span class="slider-hint">
                    Higher = brand-new stories rise to the top
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Freshness decay
                    <span class="slider-value">
                      {rankParams.recencyHalfLifeHours}h
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.recencyHalfLifeHours.min}
                    max={RANK_PREFS_BOUNDS.recencyHalfLifeHours.max}
                    step={RANK_PREFS_BOUNDS.recencyHalfLifeHours.step}
                    value={rankParams.recencyHalfLifeHours}
                    onInput={updateHalfLife}
                    aria-label="Freshness half-life in hours"
                    aria-valuetext={`${rankParams.recencyHalfLifeHours} hours`}
                  />
                  <span class="slider-hint">
                    Lower = "fresh" wears off faster
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Trending
                    <span class="slider-value">
                      {rankParams.velocityWeight.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.velocityWeight.min}
                    max={RANK_PREFS_BOUNDS.velocityWeight.max}
                    step={RANK_PREFS_BOUNDS.velocityWeight.step}
                    value={rankParams.velocityWeight}
                    onInput={updateField("velocityWeight")}
                    aria-label="Trending weight"
                    aria-valuetext={`${rankParams.velocityWeight.toFixed(2)} of ${RANK_PREFS_BOUNDS.velocityWeight.max}`}
                  />
                  <span class="slider-hint">
                    Higher = articles others are engaging with surface faster
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Authority
                    <span class="slider-value">
                      {rankParams.authorityWeight.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.authorityWeight.min}
                    max={RANK_PREFS_BOUNDS.authorityWeight.max}
                    step={RANK_PREFS_BOUNDS.authorityWeight.step}
                    value={rankParams.authorityWeight}
                    onInput={updateField("authorityWeight")}
                    aria-label="Authority weight"
                    aria-valuetext={`${rankParams.authorityWeight.toFixed(2)} of ${RANK_PREFS_BOUNDS.authorityWeight.max}`}
                  />
                  <span class="slider-hint">
                    Higher = trust established publishers more
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Variety (publishers)
                    <span class="slider-value">
                      {rankParams.publisherDiversity.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.publisherDiversity.min}
                    max={RANK_PREFS_BOUNDS.publisherDiversity.max}
                    step={RANK_PREFS_BOUNDS.publisherDiversity.step}
                    value={rankParams.publisherDiversity}
                    onInput={updateField("publisherDiversity")}
                    aria-label="Publisher variety"
                    aria-valuetext={`${rankParams.publisherDiversity.toFixed(2)} of ${RANK_PREFS_BOUNDS.publisherDiversity.max}`}
                  />
                  <span class="slider-hint">
                    Higher = avoid back-to-back from the same site
                  </span>
                </label>

                <label class="slider-row">
                  <span class="slider-label">
                    Discovery (topics)
                    <span class="slider-value">
                      {rankParams.topicDiversity.toFixed(2)}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={RANK_PREFS_BOUNDS.topicDiversity.min}
                    max={RANK_PREFS_BOUNDS.topicDiversity.max}
                    step={RANK_PREFS_BOUNDS.topicDiversity.step}
                    value={rankParams.topicDiversity}
                    onInput={updateField("topicDiversity")}
                    aria-label="Topic variety"
                    aria-valuetext={`${rankParams.topicDiversity.toFixed(2)} of ${RANK_PREFS_BOUNDS.topicDiversity.max}`}
                  />
                  <span class="slider-hint">
                    Higher = wider mix of topics, less echo chamber
                  </span>
                </label>
            </div>
          </section>

          <section class="settings-section">
            <div class="perso-head">
              <h3>Personalization</h3>
              <button
                type="button"
                role="switch"
                aria-checked={personalized}
                aria-label="Personalization"
                class={personalized ? "switch switch-on" : "switch"}
                disabled={acceptingConsent}
                onClick={togglePersonalization}
              >
                <span class="switch-track">
                  <span class="switch-thumb" />
                </span>
                <span class="switch-label">
                  {acceptingConsent ? "…" : personalized ? "On" : "Off"}
                </span>
              </button>
            </div>
            {personalized ? (
              <p class="muted">
                The feed re-ranks to your taste on this device — the sliders
                and topics above take effect, and likes/hides/dwell quietly
                tune your interests. Nothing is linked to an account. Switch
                this off to pause re-ranking (your data stays); "Forget me"
                below erases it.
              </p>
            ) : (
              <p class="muted">
                Off — the feed shows the default order and the ranking
                controls above have no effect. Switch it on to re-rank on
                this device: all learning stays local, nothing is linked to
                an account, and you can pause or "Forget me" anytime.
              </p>
            )}
          </section>

          {personalized ? (
            <>
              <section class="settings-section">
                <h3>Your topics</h3>
                <p class="muted">
                  Tap to add or remove. The feed re-ranks immediately
                  based on which tags an article carries.
                </p>
                {TOPIC_QUESTIONS.map((q) => (
                  <fieldset key={q.id} class="onboarding-question">
                    <legend>{q.prompt}</legend>
                    <div class="chips">
                      {q.options.map((o) => {
                        const on =
                          (interest[o.tag] ?? 0) >= TOPIC_ON_THRESHOLD;
                        return (
                          <button
                            key={o.tag}
                            type="button"
                            class={on ? "chip chip-on" : "chip"}
                            aria-pressed={on}
                            onClick={() => void toggleTopic(o.tag)}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </section>

              <section class="settings-section">
                <h3>Your anonymous device id</h3>
                {deviceId ? (
                  <p>
                    <code class="device-id">{deviceId}</code>
                  </p>
                ) : (
                  <p class="muted">
                    No device id yet — it's created the first time the
                    page can reach the engagement endpoint.
                  </p>
                )}
                <p class="muted">
                  This id is anonymous and paired with a device cookie. It
                  is never linked to an account.
                </p>
              </section>

              <section class="settings-section">
                <h3>
                  Blocked publishers
                  {blockedPublishers.size > 0 ? (
                    <span class="pill pill-on">
                      {blockedPublishers.size}
                    </span>
                  ) : null}
                </h3>
                {blockedPublishers.size === 0 ? (
                  <p class="muted">
                    Use the "Block all from …" button on any article to
                    add a publisher here. You'll never see it again on
                    this device.
                  </p>
                ) : (
                  (() => {
                    const sorted = Array.from(blockedPublishers).sort();
                    const q = blockedFilter.trim().toLowerCase();
                    const shown = q
                      ? sorted.filter((p) => p.toLowerCase().includes(q))
                      : sorted;
                    return (
                      <>
                        <p class="muted">Tap any chip to unblock.</p>
                        {sorted.length > BLOCKED_FILTER_THRESHOLD ||
                        blockedFilter ? (
                          <input
                            type="search"
                            class="blocked-filter"
                            placeholder="Filter blocked publishers…"
                            aria-label="Filter blocked publishers"
                            value={blockedFilter}
                            onInput={(e) =>
                              setBlockedFilter(
                                (e.currentTarget as HTMLInputElement).value,
                              )
                            }
                          />
                        ) : null}
                        {shown.length === 0 ? (
                          <p class="muted">No matches for "{blockedFilter}".</p>
                        ) : (
                          <ul class="blocked-list">
                            {shown.map((p) => (
                              <li key={p}>
                                <button
                                  class="chip chip-removable"
                                  type="button"
                                  aria-label={`Unblock ${p}`}
                                  onClick={() => unblockPublisher(p)}
                                >
                                  {p}
                                  <X size={13} aria-hidden="true" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    );
                  })()
                )}
              </section>

              <section class="settings-section">
                <h3>Forget me</h3>
                <p class="muted">
                  Removes your device id, fingerprint pairing, and
                  on-device interests. We tombstone the id on our side
                  within 24 hours (see the privacy notice).
                </p>
                <button
                  class="danger"
                  disabled={busy}
                  onClick={() => void forget()}
                >
                  {busy ? "Forgetting…" : "Forget me"}
                </button>
              </section>
            </>
          ) : null}

          <section class="settings-section about">
            <h3>About Punch List</h3>
            <p class="muted">
              Punch List is the short list of what actually matters today.
              The feed re-ranks to your taste <em>on your device</em> — your
              interests, blocks, and reading history never leave your
              browser and are never sold. No ad tech, no profiles for
              sale, no engagement traps.
            </p>
            <p class="muted">
              That's the whole philosophy: you're the reader, not the
              product. Built to one day be reader-supported, not
              advertiser-funded.
            </p>
          </section>
        </div>
      </aside>
    </div>
  );
}
